import React, { useState, useMemo } from 'react';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { BarChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import BeaconProChart, {
  OptionProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { CategoryScoreAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/CategoryScoreAdapter';
import { useLastWeek } from 'src/hooks/useLastWeek';

interface ContentBarChartProps {
  title: string;
  queryFn: () => Promise<any>;
  renderSubtitle: (data: BarChartData) => string | React.ReactNode;
}

export const ContentBarChart = (props: ContentBarChartProps) => {
  const { title, queryFn, renderSubtitle } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();

  return (
    <BeaconProChart name={`content-bar-chart-${title.toLowerCase().replace(/\s+/g, '-')}`} queryFn={queryFn}>
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: ChartDataAdapter<BarChartData>) => {
          return renderSubtitle(data.transform());
        }}
      />

      <BeaconProChart.Legend
        primaryLegendProps={{ displayName: mainTimePeriod.displayName }}
        comparisonLegendProps={{ displayName: comparisonTimePeriod.displayName }}
      />
      <BeaconProChart.Visualization
        render={(data: CategoryScoreAdapter) => {
          const { chartData } = data.transform();
          return <TopEntityChartV2 chartSeries={chartData as any} />;
        }}
      />
    </BeaconProChart>
  );
};

interface ContentDropdownBarChartProps {
  prefix: string;
  options: OptionProps[];
  renderSubtitle: (selectedOption?: string, data?: any) => string | React.ReactNode;
}

export const ContentDropdownBarChart = (props: ContentDropdownBarChartProps) => {
  const { prefix, options, renderSubtitle } = props;
  const [selectedOption, setSelectedOption] = useState(options[0].id);
  const { mainTimePeriod, comparisonTimePeriod } = useLastWeek();

  return (
    <BeaconProChart
      name={`content-dropdown-bar-chart-${prefix.toLowerCase().replace(/\s+/g, '-')}-${selectedOption}`}
      queryFn={options.find((opt) => opt.id === selectedOption)?.queryFn}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          {prefix && <Text variant="h4">{prefix}</Text>}
          <SlDropdownMenu
            options={options.map((opt) => ({
              id: opt.id,
              label: opt.label
            }))}
            buttonVariant="title"
            defaultLabel=""
            onChange={(option) => setSelectedOption(option.id)}
            selectedId={selectedOption}
          />
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter: ChartDataAdapter<BarChartData>) => {
          return renderSubtitle(selectedOption, adapter.transform());
        }}
      />

      <BeaconProChart.Legend
        renderLoading={() => {
          return (
            <Flex marginY="32px" gap={32}>
              <Flex gap={8}>
                <SlSkeleton variant="rounded" height={20} width={20} />
                <SlSkeleton variant="rounded" height={20} width={132} />
              </Flex>
              <Flex gap={8}>
                <SlSkeleton variant="rounded" height={20} width={20} />
                <SlSkeleton variant="rounded" height={20} width={132} />
              </Flex>
            </Flex>
          );
        }}
        primaryLegendProps={{ displayName: mainTimePeriod.displayName }}
        comparisonLegendProps={{ displayName: comparisonTimePeriod.displayName }}
      />
      <BeaconProChart.Visualization
        render={(data: CategoryScoreAdapter) => {
          const { chartData } = data.transform();
          return (
            <TopEntityChartV2
              chartSeries={chartData as any}
              chartOptionsOverride={{
                chart: {
                  height: 320 
                },
                yAxis: {
                  maxPadding: 0.05
                }
              }}
            />
          );
        }}
      />
    </BeaconProChart>
  );
};

interface MetricOption {
  id: string;
  label: string;
}

interface GroupByOption {
  id: string;
  label: string;
}

type QueryFnMap = {
  [metricId: string]: {
    [groupById: string]: () => Promise<any>;
  };
};

interface ContentDoubleDropdownBarChartProps {
  prefix?: string;
  metrics: MetricOption[];
  groupByFields: GroupByOption[];
  queryFnMap: QueryFnMap;
  renderSubtitle: (selectedMetric: string, selectedGroupBy: string, data: BarChartData) => string | React.ReactNode;
}

export const ContentDoubleDropdownBarChart = (props: ContentDoubleDropdownBarChartProps) => {
  const { metrics, groupByFields, queryFnMap, renderSubtitle } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const [selectedMetric, setSelectedMetric] = useState(metrics[0].id);
  const [selectedGroupBy, setSelectedGroupBy] = useState(groupByFields[0].id);

  const currentQueryFn = useMemo(() => {
    return queryFnMap[selectedMetric]?.[selectedGroupBy] || (() => Promise.resolve([]));
  }, [queryFnMap, selectedMetric, selectedGroupBy]);

  return (
    <BeaconProChart
      name={`content-double-dropdown-bar-chart-${selectedMetric}-${selectedGroupBy}`}
      queryFn={currentQueryFn}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <SlDropdownMenu
            options={metrics.map((metric) => ({
              id: metric.id,
              label: metric.label
            }))}
            buttonVariant="title"
            defaultLabel=""
            onChange={(option) => setSelectedMetric(option.id)}
            selectedId={selectedMetric}
          />
          <Text variant="h4">by</Text>
          <SlDropdownMenu
            options={groupByFields.map((groupBy) => ({
              id: groupBy.id,
              label: groupBy.label
            }))}
            buttonVariant="title"
            defaultLabel=""
            onChange={(option) => setSelectedGroupBy(option.id)}
            selectedId={selectedGroupBy}
          />
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter: ChartDataAdapter<BarChartData>) => {
          return renderSubtitle(selectedMetric, selectedGroupBy, adapter.transform());
        }}
      />

      <BeaconProChart.Legend
        primaryLegendProps={{ displayName: mainTimePeriod.displayName }}
        comparisonLegendProps={{ displayName: comparisonTimePeriod.displayName }}
      />
      <BeaconProChart.Visualization
        render={(data: CategoryScoreAdapter) => {
          const { chartData } = data.transform();
          return <TopEntityChartV2 chartSeries={chartData as any} />;
        }}
      />
    </BeaconProChart>
  );
};
